import Contact from './contact';
import Authorization from './authorization';
import React, { useEffect, useState } from 'react';
import { TFunction, WithTranslation, withTranslation } from 'react-i18next';
import fetchFormData, { IFormDataModel } from '../../../../helpers/fetchFormData';
import { createAccount, updateAccount } from '../../../../helpers/account';
import {
    accountModelFromFormData,
    EAccountType,
    IAccountModel,
} from '../../../../helpers/models/accountModel';
import { EPracticeType } from '../../../../helpers/enums/practiceType';
import { isErrorModel } from '../../../../helpers/models/errorModel';
import { ACCOUNT_ID_PARAM, REGISTRATION_ID_PARAM } from '../../../../helpers/network';
import {
    handleRequiredError,
    scrollToFormErrors,
    validateEmail,
    validatePhoneExt,
    validatePhoneNumber,
} from '../../utils/formErrors';
import { ContactFormResultKeys, IContactFormResultModel } from '../contactFormModel';
import { INotificationErrorMessage } from '../../../notification/notification';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import getProvinces from '../../../../helpers/provinces';
import getRoles from '../../../../helpers/role';
import { IFormModalRef } from '../../../modal/modal';
import getIssuers from '../../../../helpers/issuers';
import { IFormEligibilityModalRef } from '../../../modal/instances/eligibility/formEligibilityModal';

export interface IContactFormStep extends WithTranslation {
    account?: IAccountModel;
    setError: React.Dispatch<React.SetStateAction<INotificationErrorMessage[] | undefined>>;
    eligibilityRef: React.RefObject<IFormModalRef & IFormEligibilityModalRef>;
}

const ContactStep1 = (props: IContactFormStep) => {
    const { t, i18n, account, eligibilityRef } = props;

    const [provinces, setProvinces] = useState<IProvinceModel[]>([]);
    const [roles, setRoles] = useState<IRoleModel[]>([]);
    const organizationElement = document.getElementById(EPracticeType.ORGANIZATION);
    const professionalElement = document.getElementById(EPracticeType.PROFESSIONAL);
    const emailConfirmationElement = document.getElementById(ContactFormResultKeys.EMAIL_CONFIRMATION);

    useEffect(() => {
        async function getData() {
            const provincesList = await getProvinces(i18n.language);
            setProvinces(provincesList ?? []);

            const roleList = await getRoles(i18n.language);
            setRoles(roleList ?? []);
        }
        getData();
    }, [i18n.language]);

    const handleClientFormValidation = (
        formResult: IContactFormResultModel,
        data: IFormDataModel[],
        t: TFunction,
        type: number
    ) => {
        // Required fields
        const errorList = handleRequiredError(formResult, data, t);

        // Conditional field
        const fieldId =
            type === Number(EAccountType.PROFESSIONAL)
                ? ContactFormResultKeys.SPECIALIST_GROUP
                : ContactFormResultKeys.ORGANIZATION_TYPE;
        const relatedSelect = document.getElementById(fieldId) as HTMLSelectElement;
        if (type !== undefined) {
            if (!relatedSelect.value) {
                relatedSelect.classList.add('registration-error');
                document.getElementById(fieldId + '_error_logo')!.style.display = 'unset';
                const error =
                    type === Number(EAccountType.PROFESSIONAL)
                        ? {
                              message: ContactFormResultKeys.SPECIALIST_GROUP + '_error',
                              focusElement: ContactFormResultKeys.SPECIALIST_GROUP,
                              weight: 8,
                          }
                        : {
                              message: ContactFormResultKeys.ORGANIZATION_TYPE + '_error',
                              focusElement: ContactFormResultKeys.ORGANIZATION_TYPE,
                              weight: 8,
                          };
                errorList.push(error);
            } else {
                document.getElementById(fieldId + '_error_logo')!.style.display = 'none';
                relatedSelect.classList.remove('registration-error');
            }
        } else {
            errorList.push({
                message: ContactFormResultKeys.PRACTICE + '_error',
                focusElement: EPracticeType.PROFESSIONAL,
                weight: 6,
            });
        }
        const practiceTypeValue = document.querySelector(
            'input[name="registration_contact_practice"]:checked'
        );
        document.getElementById('registration-radio-button-error')!.style.display =
            practiceTypeValue ? 'none' : 'flex';

        if (!practiceTypeValue) {
            organizationElement?.classList.add('registration-error');
            organizationElement?.setAttribute('aria-invalid', 'true');
            organizationElement?.setAttribute('aria-live', 'polite');
            organizationElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('registration_contact_practice_error')}`);

            professionalElement?.classList.add('registration-error');
            professionalElement?.setAttribute('aria-invalid', 'true');
            professionalElement?.setAttribute('aria-live', 'polite');
            professionalElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('registration_contact_practice_error')}`);
        } else {
            organizationElement?.classList.remove('registration-error');
            organizationElement?.removeAttribute('aria-invalid');
            organizationElement?.removeAttribute('aria-live');
            organizationElement?.removeAttribute('aria-label');

            professionalElement?.classList.remove('registration-error');
            professionalElement?.removeAttribute('aria-invalid');
            professionalElement?.removeAttribute('aria-live');
            professionalElement?.removeAttribute('aria-label');
        }

        // Authorization
        const authorization = data.filter((element: IFormDataModel) => {
            return (
                element.name === 'form_authorization_terms' ||
                element.name === 'form_authorization_eligible' ||
                element.name === 'form_authorization_legal_terms'
            );
        });

        authorization.forEach((element, index: number) => {
            const authorizationElement = document.getElementById(element.name);
            const authorizationElementLabel = document.getElementById(element.name + '_label');

            if (!element.value) {
                authorizationElementLabel?.classList.add('registration-error');
                authorizationElement?.setAttribute('aria-invalid', 'true');
                authorizationElement?.setAttribute('aria-live', 'polite');
                authorizationElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t(element.name + '_error')}`);

                document.getElementById(element.name + '-error')!.style.display = 'flex';
                errorList.push({
                    message: element.name + '_error',
                    focusElement: element.name,
                    weight: 9 + index,
                });
            } else {
                document.getElementById(element.name + '-error')!.style.display = 'none';
                authorizationElementLabel?.classList.remove('registration-error');
                authorizationElement?.removeAttribute('aria-invalid');
                authorizationElement?.removeAttribute('aria-live');
                authorizationElement?.removeAttribute('aria-label');
            }
        });
        if (
            formResult.registration_contact_email !== formResult.registration_contact_email_confirm
        ) {
            emailConfirmationElement?.classList.add('registration-error');
            emailConfirmationElement?.setAttribute('aria-invalid', 'true');
            emailConfirmationElement?.setAttribute('aria-live', 'polite');
            emailConfirmationElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t(ContactFormResultKeys.EMAIL_CONFIRMATION + '_error')}`);
            errorList.push({
                message: ContactFormResultKeys.EMAIL_CONFIRMATION + '_error',
                focusElement: ContactFormResultKeys.EMAIL_CONFIRMATION,
                weight: 5,
            });
        } else {
            emailConfirmationElement?.classList.remove('registration-error');
            emailConfirmationElement?.removeAttribute('aria-invalid');
            emailConfirmationElement?.removeAttribute('aria-live');
            emailConfirmationElement?.removeAttribute('aria-label');
        }

        // Custom validation
        const extValidation = validatePhoneExt(
            ContactFormResultKeys.PHONE_EXT,
            formResult.registration_contact_phone_ext,
            t
        );
        if (formResult.registration_contact_phone_ext) {
            if (extValidation) {
                errorList.push({
                    message: extValidation,
                    focusElement: ContactFormResultKeys.PHONE_EXT,
                    weight: 3,
                });
            }
        }

        if (formResult.registration_contact_email) {
            const emailValidation = validateEmail(
                ContactFormResultKeys.EMAIL,
                formResult.registration_contact_email,
                t
            );
            if (emailValidation) {
                errorList.push({
                    message: emailValidation,
                    focusElement: ContactFormResultKeys.EMAIL,
                    weight: 4,
                });
            }
        }
        if (formResult.registration_contact_phone) {
            const phoneValidation = validatePhoneNumber(
                ContactFormResultKeys.PHONE,
                formResult.registration_contact_phone,
                t
            );
            if (phoneValidation) {
                errorList.push({
                    message: phoneValidation,
                    focusElement: ContactFormResultKeys.PHONE,
                    weight: 2,
                });
            }
        }
        return errorList;
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (e.nativeEvent.submitter.id === 'eligibility-submit-button') {
            return;
        }

        const data = fetchFormData(e);
        const formResult = Object.assign(
            {},
            ...data.map((x) => ({ [x.name]: x.value }))
        ) as IContactFormResultModel;
        const accountDetails = accountModelFromFormData(formResult, 1);

        data.filter((element) => element?.checked === true).map((el) => {
            if (el.value === EPracticeType.PROFESSIONAL) {
                accountDetails.type = Number(EAccountType.PROFESSIONAL);
                accountDetails.role = +formResult.registration_specialist_group_select;
            } else if (el.value === EPracticeType.ORGANIZATION) {
                accountDetails.type = Number(EAccountType.ORGANIZATION);
                accountDetails.role = +formResult.registration_organization_type_select;
            }
            return { ...accountDetails };
        });

        const issuerList = await getIssuers(
            i18n.language,
            Number(accountDetails.role),
            +accountDetails.province
        );

        if (!issuerList?.length) {
            eligibilityRef.current?.setModalOpen(true);
            eligibilityRef.current?.setSelectedProvince(accountDetails?.province?.toString() ?? '')
            eligibilityRef.current?.setSelectedRole(accountDetails?.role?.toString() ?? '');

            return;
        }

        const errorList = handleClientFormValidation(formResult, data, t, accountDetails.type);

        if (!errorList.length) {
            accountDetails.id = props.account ? props.account.id : '';

            // If the selected role is "Optician" in the professional flow, we redirect to the organization flow.
            const roleId = Number.isInteger(accountDetails.role)
                ? accountDetails.role
                : (accountDetails.role as IRoleModel).id;
            if (accountDetails.type === EAccountType.PROFESSIONAL && roleId === 5) {
                accountDetails.type = EAccountType.ORGANIZATION;
            }

            const account = props.account
                ? await updateAccount(i18n.language, props.account!.id, accountDetails)
                : await createAccount(i18n.language, accountDetails);
            if (isErrorModel(account) && 'statusCode' in account) {
                if (account.statusCode === 401) {
                    errorList.push({
                        messagePrefix: t('form_contact_error_email_1', { accountDetails }),
                        message: t('form_contact_error_email_2'),
                        messageSuffix: t('form_contact_error_email_3'),
                        focusElement: t('form_contact_error_email_focus_element'),
                        weight: 0,
                        email: accountDetails.email
                    });
                } else {
                    errorList.push({
                        messagePrefix: '',
                        message: account.message,
                        messageSuffix: '',
                        focusElement: '',
                        weight: 0,
                        email: ''
                    });
                }
            } else {
                window.location.search = `?${ACCOUNT_ID_PARAM}=${
                    (account as IAccountModel)!.id
                }&${REGISTRATION_ID_PARAM}=${(account as IAccountModel)!.registrationId}`;
            }
        }

        if (errorList.length) {
            props.setError(errorList);
            scrollToFormErrors();
        }
    };

    return (
        <form className="registration-container" onSubmit={handleSubmit}>
            <Contact provinces={provinces} roles={roles} account={account} />
            <Authorization provinces={provinces} roles={roles} eligibilityRef={eligibilityRef} />
            <button className="button button--primary registration-submit-button">
                {t('form_contact_submit_title')}
            </button>
        </form>
    );
};

export default withTranslation()(ContactStep1);
